import type { ReactElement } from 'react';
import type { DocumentSectionType } from '@shared/types';
import { PageShell, PageTitle } from '../Page';
import type { PageProps } from '../types';
import { EditableBullets, EditableText } from '../../components/Editable';
import { ImageSlot } from '../../components/ImageSlot';
import { useContentLibrary } from '../../state/ContentLibraryContext';
import { knobClasses } from './knobs';
import { Frame, HairlineList, focalPointStyle } from './primitives';
import { WhyUsArt } from './WhyUsArt';

const THANK_YOU_HEADLINE_DEFAULT = "Let's put the plan to work.";
const THANK_YOU_BODY_DEFAULT = 'A focused plan built to turn this budget into results.';

/**
 * why_us (docs/momentum-v3-slides.md `why_us`; slide audit Slide 21): SPLIT
 * dark, unchanged from v2 apart from the zero-truncation rule and the photo
 * treatment. Heading, body, and 3-4 proof points as a hairline list bind
 * exactly like the standard/momentum_v1 versions. The image is agent-set
 * (`content.imageUrl`, a Pexels team/client-service photo) - v3 no longer
 * has a baked-in kit photo fallback (that asset could carry another deck's
 * branding); absent an image, the slot renders `WhyUsArt` (K3c) - a themed
 * brand-palette treatment built from this deck's own `--mv3-mint`/`--mv3-
 * lime` tokens - rather than an empty flat rectangle, so the slide never
 * reads as unfinished.
 */
export function WhyUsPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const { library } = useContentLibrary();
  const fallback = library.staticSections[section.type as DocumentSectionType];
  const body = section.content.body ?? fallback?.body;
  const proofPoints =
    section.content.bullets && section.content.bullets.length > 0 ? section.content.bullets : fallback?.bullets;
  const imageUrl = section.content.imageUrl;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-whyus-root"
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-whyus-grid">
          <div className="mv3-whyus-left">
            <PageTitle
              title={section.title}
              readOnly={readOnly}
              onSave={(title) => actions.saveTitle(section, title)}
              className="mv3-whyus-title"
            />
            <div className="mv3-whyus-body">
              <EditableText
                value={body}
                field="body"
                placeholder="Add copy"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { body: next })}
              />
            </div>
            {readOnly ? (
              proofPoints && proofPoints.length > 0 ? (
                <HairlineList items={proofPoints.slice(0, 4)} className="mv3-whyus-proof" />
              ) : null
            ) : (
              <EditableBullets
                value={proofPoints}
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { bullets: next })}
              />
            )}
          </div>
          <div className="mv3-whyus-hero">
            {imageUrl ? (
              <img src={imageUrl} alt="Why us" className="mv3-whyus-image" style={focalPointStyle(section)} />
            ) : (
              <WhyUsArt />
            )}
            {!readOnly ? (
              <ImageSlot
                url={imageUrl}
                alt="Why us"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                className="mv3-whyus-image-affordance"
                hideImage
                proposalId={proposal.proposalId}
                defaultQuery={section.title}
              />
            ) : null}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}

/**
 * thank_you (docs/momentum-v3-slides.md `thank_you`; slide audit Slide 24):
 * SPLIT dark closing statement - rebuilt from v2's version, which the audit
 * flagged for a generic "Thank you" headline, an internal-sounding process
 * line, and an extra overlay card with its own pill. v3 keeps only what the
 * audit's simplified layout asks for: headline, one supporting sentence, the
 * image, and the persistent footer (`Frame`'s own `Footer`, not drawn here).
 * No pinstripes (handled once, for every v3 slide, in `Frame`), no "Thank
 * you" pill, no "Strategy -> Activation -> Learning" breadcrumb, no overlay
 * next-steps card or CTA pill. Headline/body are agent-authored
 * (`content.headline` / `content.body`); the hardcoded defaults below are
 * the last-resort fallback before the agent writes a closing line, never a
 * stand-in for one once it has.
 */
export function ThankYouPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const imageUrl = section.content.imageUrl;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className={['mv3-thankyou-root', ...knobClasses(section)].join(' ')}
      hideTitle
    >
      <Frame surface="dark" proposal={proposal} section={section}>
        <div className="mv3-thankyou-grid">
          <div className="mv3-thankyou-left">
            <div className="mv3-thankyou-headline">
              <EditableText
                value={section.content.headline || THANK_YOU_HEADLINE_DEFAULT}
                field="headline"
                placeholder={THANK_YOU_HEADLINE_DEFAULT}
                readOnly={readOnly}
                variant="headline"
                onSave={(headline) => actions.saveContent(section, { headline })}
              />
            </div>
            <div className="mv3-thankyou-body">
              <EditableText
                value={section.content.body || THANK_YOU_BODY_DEFAULT}
                field="body"
                placeholder={THANK_YOU_BODY_DEFAULT}
                readOnly={readOnly}
                onSave={(body) => actions.saveContent(section, { body })}
              />
            </div>
          </div>
          <div className="mv3-thankyou-hero">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Closing"
                className="mv3-thankyou-image"
                style={focalPointStyle(section)}
              />
            ) : (
              <div className="mv3-thankyou-image mv3-image-empty-dark" aria-hidden="true" />
            )}
            {!readOnly ? (
              <ImageSlot
                url={imageUrl}
                alt="Closing"
                readOnly={readOnly}
                onSave={(next) => actions.saveContent(section, { imageUrl: next })}
                className="mv3-thankyou-image-affordance"
                hideImage
                proposalId={proposal.proposalId}
                defaultQuery={proposal.advertiser.industry ?? section.title}
              />
            ) : null}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
